import { type XmlNodeArrayT, type XmlNodeT, xmlNode } from '../xml';

import { DEF_TRANSFORMATION_STAGES } from './definitions';

// Transformation stage definitions are intended for injection into AI prompts,
// to specify to AI what valid transformation stages we have, and how exactly
// we define them.

type TransformationStageDefinitionT = {
  description: string;
  name: string;
};

export const TRANSFORMATION_STAGE_KINDS = [
  'BUILD',
  'INTEGRATE',
  'MOBILIZE',
  'RECOGNIZE',
  'REGULATE',
  'RELEASE',
] as const;

export type TransformationStageKindT
  = typeof TRANSFORMATION_STAGE_KINDS[number];

export const TRANSFORMATION_STAGES:
Record<TransformationStageKindT, TransformationStageDefinitionT> = {
  BUILD: {
    description: `
Strengthens and reinforces the specific capacity or state one needs.

It is useful when one needs to develop or reinforce:
- Focus.
- Confidence.
- Energy.
- Resilience.
- Calm.
- Presence.
- Creativity.
- Courage.
- Emotional steadiness.
- Physical or mental readiness.`,
    name: 'Build',
  },
  INTEGRATE: {
    description: `
Helps one to absorb the transformation, to embody the intended state, and to transition into the moment ahead.

It is useful when:
- One needs to stabilize the change.
- One needs to carry the intended state into the next moment.
- One is completing an emotional or restorative experience.
- One needs a deliberate transition into sleep, work, performance, or daily life.`,
    name: 'Integrate',
  },
  MOBILIZE: {
    description: `
Activates energy, attention, movement, motivation, or readiness.

It is useful when:
- One feels sluggish, fatigued, flat, or unmotivated.
- One needs increased alertness.
- One is preparing for physical or mental performance.
- One needs to shift from reflection or recovery into action.`,
    name: 'Mobilize',
  },
  RECOGNIZE: {
    description: `
Helps to notice and understand one's current physical, mental, emotional, or energetic state.

It is useful when:
- One is disconnected from what they are feeling.
- One feels confused or uncertain.
- One needs greater body or emotional awareness.
- One needs to become present before changing their state.`,
    name: 'Recognize',
  },
  REGULATE: {
    description: `
Establishes sufficient safety, stability, presence, and nervous-system balance.

It is useful when:
- One is anxious, overwhelmed, scattered, or overstimulated.
- One is emotionally or physiologically dysregulated.
- One needs to settle before moving into another stage.
- One needs to return to a manageable baseline.`,
    name: 'Regulate',
  },
  RELEASE: {
    description: `
Reduces or discharges tension, stress, emotional weight, restrictive patterns, or excess activation.

It is useful when:
- One is carrying physical tension.
- One is holding residual stress or emotional weight.
- One needs to let go of a previous experience.
- One is sufficiently regulated to safely release what they are carrying.`,
    name: 'Release',
  },
};

export function xmlTransformationStageDefinition(
  kind: TransformationStageKindT,
): XmlNodeT<'transformation_stage_definition'> {
  const stage = TRANSFORMATION_STAGES[kind];
  return xmlNode('transformation_stage_definition', stage.description, {
    kind,
    name: stage.name,
  });
}

export function xmlTransformationStageDefinitions(): XmlNodeT<'transformation_stage_definitions'> {
  const nodes: XmlNodeArrayT = [];

  for (const kind of TRANSFORMATION_STAGE_KINDS) {
    nodes.push(xmlTransformationStageDefinition(kind));
  }

  return xmlNode(
    'transformation_stage_definitions',
    nodes,
    DEF_TRANSFORMATION_STAGES,
  );
}

// The following types are used store on related (Video, Experience) objects
// the information about their transformation stages, and to describe this
// information for AI purposes.

export type TransformationStageT = {
  durationMin: number;
  kind: TransformationStageKindT;
};

/**
 * Comparse two sequences of transformation stages, returns "true" if they are
 * completely identical; otherwise returns "false".
 */
export function areEqualTransformationStages(
  a: TransformationStageT[],
  b: TransformationStageT[],
): boolean {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    const ai = a[i];
    const bi = b[i];
    if (
      ai?.durationMin !== bi?.durationMin
      || ai?.kind !== bi?.kind
    ) return false;
  }

  return true;
}

export function xmlTransformationStage(
  stage: TransformationStageT,
): XmlNodeT<'transformation_stage'> {
  return xmlNode('transformation_stage', '', {
    duration: `${stage.durationMin} minutes`,
    kind: stage.kind,
  });
}

export function xmlTransformationStages(
  stages: TransformationStageT[],
): XmlNodeT<'transformation_stages'> {
  return xmlNode(
    'transformation_stages',
    stages.map(xmlTransformationStage),
    DEF_TRANSFORMATION_STAGES,
  );
}

/**
 * Concatenates together two arrays of transformation stages, without modifying
 * original arrays / objects.
 */
export function concatTransformationStages(
  a: TransformationStageT[],
  b: TransformationStageT[],
): TransformationStageT[] {
  if (!a.length) return b;
  if (!b.length) return a;

  const aLastIdx = a.length - 1;
  const aLast = a[aLastIdx];
  if (!aLast) throw Error('Internal error');

  if (aLast.kind !== b[0]?.kind) return a.concat(b);

  const res = a.concat(b.slice(1));

  res[aLastIdx] = {
    durationMin: aLast.durationMin + b[0].durationMin,
    kind: aLast.kind,
  };

  return res;
}
